import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { listCarLogs } from "../../api/carLog";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";

// ✅ รองรับทั้ง MySQL (id: number) และ MongoDB (_id: string)
export interface CarLog {
  id?: number;
  _id?: string;
  carId: number;
  plateNumber?: string;
  gpsID?: string;
  engine?: string;
  speed?: number;
  lat: number;
  lng: number;
  snappedLat?: number;
  snappedLng?: number;
  segIndex?: number;
  distSoFar?: number;
  timestamp?: number;
  createdAt?: string;
}

export default function CarLogListPage() {
  const [logs, setLogs] = useState<CarLog[]>([]);
  const [plateNumber, setPlateNumber] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const { id } = useParams<{ id: string }>();

  // ✅ โหลดข้อมูล logs (รองรับกรองวันที่)
  const load = async () => {
    if (!id) return;

    const params: any = {};
    if (startDate) params.start = startDate.format("YYYY-MM-DD");
    if (endDate) params.end = endDate.format("YYYY-MM-DD");

    try {
      const data = await listCarLogs(Number(id), params);

      const normalized = data.map((log: any) => ({
        ...log,
        id: log.id ?? log._id,
      }));

      setLogs(normalized);

      if (normalized.length > 0 && normalized[0].plateNumber) {
        setPlateNumber(normalized[0].plateNumber);
      }
    } catch (err: any) {
      Swal.fire("ผิดพลาด", err.message || "ไม่สามารถโหลดข้อมูลได้", "error");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  // ✅ DataGrid columns
  const columns: GridColDef[] = [
    {
      field: "timestamp",
      headerName: "วันที่ (Device)",
      width: 180,
      valueGetter: (params: any) => {
        const dt = params;
        return dt ? new Date(dt).toLocaleString("th-TH") : "-";
      },
    },
    { field: "gpsID", headerName: "GPS ID", width: 120 },
    { field: "plateNumber", headerName: "ทะเบียนรถ", width: 120 },
    { field: "engine", headerName: "Engine", width: 100 },
    {
      field: "speed",
      headerName: "ความเร็ว (km/h)",
      width: 140,
      valueFormatter: ( params ) =>
        params != null ? Number(params).toFixed(2) : "-",
    },
    {
      field: "distSoFar",
      headerName: "ระยะทางรวม (m)",
      width: 150,
      valueFormatter: (params) =>
        params != null ? Number(params).toFixed(2) : "-",
    },
    { field: "segIndex", headerName: "Segment", width: 100 },
    { field: "lat", headerName: "Lat", flex: 1 },
    { field: "lng", headerName: "Lng", flex: 1 },
    { field: "snappedLat", headerName: "SnapLat", flex: 1 },
    { field: "snappedLng", headerName: "SnapLng", flex: 1 },
  ];

  // ✅ export Excel
  const handleExportExcel = () => {
    if (logs.length === 0) {
      Swal.fire("ไม่มีข้อมูล", "ไม่พบ log ที่จะ export", "info");
      return;
    }

    const exportData = logs.map((log) => ({
      เวลาServer: log.createdAt
        ? new Date(log.createdAt).toLocaleString("th-TH")
        : "-",
      เวลาDevice: log.timestamp
        ? new Date(log.timestamp).toLocaleString("th-TH")
        : "-",
      GPS_ID: log.gpsID ?? "-",
      Engine: log.engine ?? "-",
      ความเร็ว: log.speed ?? 0,
      ระยะทาง: log.distSoFar ?? 0,
      Segment: log.segIndex ?? "-",
      Lat: log.lat,
      Lng: log.lng,
      SnapLat: log.snappedLat,
      SnapLng: log.snappedLng,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const filename = plateNumber
      ? `logs_${plateNumber}.xlsx`
      : `car_${id}_logs.xlsx`;

    saveAs(blob, filename);
  };

  return (
    <Box>
      {/* ✅ ส่วนหัว */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h6">
          Log รถคันที่ {id} {plateNumber ? `(${plateNumber})` : ""}
        </Typography>

        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <DatePicker
            label="วันที่เริ่ม"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            format="YYYY-MM-DD"
            slotProps={{
              textField: { size: "small" },
            }}
          />
          <DatePicker
            label="ถึงวันที่"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            format="YYYY-MM-DD"
            slotProps={{
              textField: { size: "small" },
            }}
          />
          <Button
            variant="contained"
            onClick={load}
            sx={{ backgroundColor: "#FEDC00", color: "#000" }}
          >
            โหลดข้อมูล
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportExcel}
            sx={{ borderColor: "#FEDC00", color: "#000" }}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* ✅ ตาราง */}
      <Paper sx={{ height: 550, width: "100%", p: 1 }}>
        <DataGrid
          rows={logs}
          columns={columns}
          pageSizeOptions={[ 25, 50, 75, 100]}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#FEDC00",
              color: "#000",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#FFFDE7",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#FFF59D",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #eee",
            },
          }}
        />
      </Paper>
    </Box>
  );
}
